import { isAllowedHost } from './allowlist';
import type { AdSlotSummary, DetectorOptions, MalvertisingHit } from './types';

const AD_SLOT_SELECTOR = 'ins.\\31 8ad69c2, ins[data-key]';

interface SlotInfo {
    key: string | null;
    testId: string | null;
    src: string | null;
    srcKey: string | null;
    host: string | null;
    outerHtml: string | null;
}

/** Turn a hit field into a string for TrackJS metadata. Arrays become newline-joined lists (readable
 *  in the dashboard); objects in an array are JSON-encoded per line (used by allSlots); null becomes
 *  the literal "null". */
function serializeMetaValue(value: unknown): string {
    if (value === null || value === undefined) return 'null';
    if (Array.isArray(value)) {
        if (!value.length) return '(none)';
        return value.map((v) => (typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v))).join('\n');
    }
    return String(value);
}

/**
 * Detects forced ad redirects (malvertising) and reports them to TrackJS (native agent,
 * lazy-loaded on a hit). The observed campaign auto-redirects on page load with no user gesture.
 *
 * CONFIRMED MECHANISM (deobfuscated live C2 payload, 2026-07-01 — see docs/malvertising-repro/malvertising_report.md):
 * the creative sprays several TOP-navigation vectors, NOT window.open:
 *   1. window.top.location.href = re_l_url            (click_type=hj)
 *   2. window.top.eval("location.href = re_l_url")    (click_type=eval)
 *   3. synthetic <a target="_top" href=re_l_url>.click()  (click_type=pop)
 *   4. hidden <input> + blur-event fallback trigger
 *
 * VECTORS (matched to the above):
 *  - anchor-click (capture phase): catches #3 BEFORE navigation → full target URL + click_type.
 *  - navigate-api (Navigation API `navigate`): catches #1/#2 where supported → full target URL.
 *  - pagehide backstop: catches any top-nav teardown (no target URL).
 *  - focus-steal (window blur / tab hidden): catches a focus-stealing popup (no target URL).
 *  - window.open: cheap catch-all; this campaign does NOT use it.
 *
 * The URL-bearing vectors (anchor-click / navigate-api / window.open) have a concrete foreign target,
 * so they report directly. The backstop vectors (focus-steal / pagehide) have NO target URL and are
 * FP-prone, so they only fire when the page ALSO pulled in a non-allowlisted resource
 * (pageLooksSuspicious) — the forced redirect always fetches from its C2 first, whereas a clean page
 * the user merely tabbed away from has none.
 *
 * WHICH VECTOR ACTUALLY FIRES IN PRODUCTION IS UNCERTAIN. The ad slot's iframe sandbox
 * (postgen_ads.js: `allow-popups allow-popups-to-escape-sandbox ...`, NO `allow-top-navigation`)
 * would BLOCK the top-navigation vectors (#1/#2/#3) and force the redirect through an escaping
 * popup — which only focus-steal can catch. So focus-steal is kept, not dropped. The AV slot filler
 * (deliver.ptgncdn.com/5a47a834.js) is third-party; confirm the real vector against a live creative.
 *
 * SCOPE & LIMITATIONS (deliberate — NOT a universal redirect catcher):
 *  - NOT CAUGHT (to keep false positives near zero): redirects AFTER a real user interaction
 *    (suppressed intentionally, including a user clicking the ad iframe itself); redirects later than
 *    autoRedirectWindowMs; navigation entirely inside the cross-origin ad iframe with no observable
 *    side-effect on our page; a focus-steal / teardown on a page with no suspicious resource.
 *  - We CANNOT read the cross-origin iframe's rendered content or nested iframes (SecurityError).
 *    "What's inside" is approximated via resource-timing (every URL any frame loads) — see
 *    observeResources() / MalvertisingHit.suspiciousResources.
 *  - We usually CANNOT single out the offending slot (its iframe host is the allowlisted provider),
 *    so EVERY slot is captured in MalvertisingHit.allSlots; the real zone key lives on one <ins data-key>.
 */
export class MalvertisingDetector {
    private readonly options: DetectorOptions;
    private userInteracted = false;
    private readonly loadTime = performance.now();
    private reported = false;
    private originalOpen: typeof window.open;
    /** The wrapper we install on window.open, so stop() can check it's still ours before restoring. */
    private wrappedOpen: typeof window.open | null = null;
    /** Rolling set of non-allowlisted resource URLs seen anywhere on the page. Bounded to avoid growth. */
    private readonly suspiciousResources = new Set<string>();
    private static readonly MAX_RESOURCES = 40;

    // Bound handler references so stop() can detach them (production hygiene + test isolation).
    private readonly onInteract = (): void => {
        this.userInteracted = true;
    };
    private readonly onClickBound = (e: Event): void => this.onClick(e);
    private readonly onNavigateBound = (e: Event): void => this.onNavigate(e);
    private readonly onUnloadBound = (): void => this.onUnload();
    // A capture-phase blur listener on window ALSO receives descendant element blurs (form fields, etc.),
    // which are not focus-steals; only a blur of the window itself means focus left the page.
    private readonly onBlurBound = (e: Event): void => {
        if (e.target === window) this.onFocusSteal();
    };
    private readonly onVisibilityBound = (): void => {
        if (document.visibilityState === 'hidden') this.onFocusSteal();
    };
    private navTarget: EventTarget | null = null;

    constructor(options: DetectorOptions) {
        this.options = options;
        this.originalOpen = window.open.bind(window);
    }

    start(): void {
        this.observeResources();

        // First real user gesture disarms the auto-redirect heuristic.
        window.addEventListener('pointerdown', this.onInteract, { capture: true, once: true });
        window.addEventListener('keydown', this.onInteract, { capture: true, once: true });

        // PRIMARY vector #1 — synthetic <a target="_top"> click (campaign click_type=pop).
        // We catch it in the CAPTURE phase, before navigation, so we read the full target URL.
        document.addEventListener('click', this.onClickBound, { capture: true });

        // PRIMARY vector #2 — Navigation API `navigate` event (where supported). Fires for
        // window.top.location = … and eval'd location sets (click_type=hj / eval) with the URL.
        const nav = (window as unknown as { navigation?: EventTarget }).navigation;
        if (nav && typeof nav.addEventListener === 'function') {
            this.navTarget = nav;
            nav.addEventListener('navigate', this.onNavigateBound);
        }

        // BACKSTOP — top-level navigation away with no interaction, via pagehide (catches the teardown
        // of any top-nav mechanism whose URL we can't read). Deliberately NOT beforeunload: it disables
        // the bfcache for the whole AV segment, and pagehide covers the same teardown.
        window.addEventListener('pagehide', this.onUnloadBound, { capture: true });

        // BACKSTOP — focus-steal (window blur / tab hidden): a popup we cannot intercept.
        window.addEventListener('blur', this.onBlurBound, { capture: true });
        document.addEventListener('visibilitychange', this.onVisibilityBound, { capture: true });

        // CATCH-ALL — window.open. The confirmed campaign does NOT use it, but it is cheap to keep
        // and covers other popup-based malvertising.
        this.wrappedOpen = (...args: Parameters<typeof window.open>): ReturnType<typeof window.open> => {
            const url = args[0];
            if (url) {
                this.evaluate(String(url), 'window.open');
            }
            return this.originalOpen(...args);
        };
        window.open = this.wrappedOpen;
    }

    /** Detach all listeners and restore window.open. Called after a report (one-shot) and available
     *  for cleanup/tests. In production the page reload re-creates the detector fresh. */
    stop(): void {
        window.removeEventListener('pointerdown', this.onInteract, { capture: true });
        window.removeEventListener('keydown', this.onInteract, { capture: true });
        document.removeEventListener('click', this.onClickBound, { capture: true });
        this.navTarget?.removeEventListener('navigate', this.onNavigateBound);
        window.removeEventListener('pagehide', this.onUnloadBound, { capture: true });
        window.removeEventListener('blur', this.onBlurBound, { capture: true });
        document.removeEventListener('visibilitychange', this.onVisibilityBound, { capture: true });
        // Only restore if window.open is still OUR wrapper — another script (e.g. a popunder) may have
        // wrapped it after we did, and clobbering that would silently break it.
        if (window.open === this.wrappedOpen) window.open = this.originalOpen;
    }

    /** Capture-phase click handler: catch a synthetic anchor whose target is _top/_blank and whose
     *  href is a foreign host, before it navigates. This is the campaign's click_type=pop vector. */
    private onClick(event: Event): void {
        if (this.reported) return;
        const path = (event.composedPath?.() ?? []) as EventTarget[];
        const anchor = path.find((el): el is HTMLAnchorElement => el instanceof HTMLAnchorElement && !!el.href);
        if (!anchor) return;

        // A real user click sets userInteracted via pointerdown first; a synthetic .click() does not.
        const synthetic = !(event as { isTrusted?: boolean }).isTrusted;
        if (!synthetic && this.userInteracted) return;

        const target = (anchor.getAttribute('target') ?? '').toLowerCase();
        if (target !== '_top' && target !== '_blank' && target !== '_parent') return;
        this.evaluate(anchor.href, 'anchor-click');
    }

    private onNavigate(event: Event): void {
        if (this.reported) return;
        const dest = (event as { destination?: { url?: string } }).destination;
        const url = dest?.url;
        if (!url) return;
        this.evaluate(url, 'navigate-api');
    }

    /** Watch resource-timing entries for every network request made by ANY frame on the page,
     *  including inside cross-origin ad iframes and their nested frames. We can't read the iframe
     *  DOM, but we CAN see what it loads — which reconstructs the creative/redirect chain
     *  (e.g. magsrv/exoclick -> C2 -> landing page). Records only non-allowlisted hosts. */
    private observeResources(): void {
        if (typeof PerformanceObserver === 'undefined') return;
        try {
            const obs = new PerformanceObserver((list) => {
                for (const entry of list.getEntries()) {
                    const url = (entry as PerformanceResourceTiming).name;
                    if (!url) continue;
                    let host: string;
                    try {
                        host = new URL(url, window.location.href).host;
                    } catch {
                        continue;
                    }
                    if (!host || isAllowedHost(host, this.options.allowedHosts)) continue;
                    if (this.suspiciousResources.size < MalvertisingDetector.MAX_RESOURCES) {
                        this.suspiciousResources.add(url);
                    }
                }
            });
            obs.observe({ type: 'resource', buffered: true });
        } catch {
            // resource timing unavailable — the rest of the detector still works
        }
    }

    private onFocusSteal(): void {
        if (this.userInteracted || this.reported) return;
        if (performance.now() - this.loadTime > this.options.autoRedirectWindowMs) return;
        // Focus moving INTO an ad-slot iframe is the user clicking the ad, not malware stealing focus
        // (the pointerdown happened inside the cross-origin frame, so we never saw it). Treat as interaction.
        if (this.isAdIframeFocused()) {
            this.userInteracted = true;
            return;
        }
        // Otherwise this is a focus-stealing popup OR a benign tab switch — indistinguishable on its own.
        // Only treat it as a hit if the page actually pulled in a suspicious resource (see pageLooksSuspicious).
        if (!this.pageLooksSuspicious()) return;
        this.report({ vector: 'focus-steal', targetUrl: '(cross-origin popup)', targetHost: '(unknown)' });
    }

    private onUnload(): void {
        if (this.userInteracted || this.reported) return;
        // Only meaningful very close to load; a genuine same-site nav later isn't malvertising.
        if (performance.now() - this.loadTime > this.options.autoRedirectWindowMs) return;
        if (!this.pageLooksSuspicious()) return;
        // KNOWN LIMITATION: a report started during teardown is best-effort — if the TrackJS agent isn't
        // already installed it may not finish loading (fixable later via a localStorage flush on next load).
        this.report({ vector: 'unload', targetUrl: '(top navigation)', targetHost: '(unknown)' });
    }

    private evaluate(rawUrl: string, vector: MalvertisingHit['vector']): void {
        if (this.reported) return;

        let host: string;
        try {
            host = new URL(rawUrl, window.location.href).host;
        } catch {
            return;
        }

        if (!host || isAllowedHost(host, this.options.allowedHosts)) return;

        // A popup to a foreign host is only "forced" if the user hadn't interacted and it's
        // close to load. Genuine ad clicks set userInteracted=true and are excluded.
        const withinWindow = performance.now() - this.loadTime <= this.options.autoRedirectWindowMs;
        if (this.userInteracted || !withinWindow) return;

        this.report({ vector, targetUrl: rawUrl, targetHost: host });
    }

    /** Identify the offending ad slot: the slot whose child iframe points at a NON-allowlisted host.
     *  We deliberately do NOT fall back to a slot with an allowlisted (legit) iframe — blaming a clean
     *  slot is worse than reporting "unknown", especially for the focus-steal vector where we have no
     *  target URL to corroborate. Returns nulls if no foreign-iframe slot is found. */
    private findOffendingSlot(): SlotInfo {
        const slots = Array.from(document.querySelectorAll<HTMLElement>(AD_SLOT_SELECTOR));

        for (const slot of slots) {
            const iframe = slot.querySelector('iframe');
            const src = iframe?.getAttribute('src') ?? null;
            if (!src) continue;

            try {
                const parsed = new URL(src, window.location.href);
                if (parsed.host && !isAllowedHost(parsed.host, this.options.allowedHosts)) {
                    return this.describeSlot(slot, src, parsed);
                }
            } catch {
                // Unparseable iframe src is itself suspicious → treat as offending.
                return this.describeSlot(slot, src, null);
            }
        }

        return { key: null, testId: null, src: null, srcKey: null, host: null, outerHtml: null };
    }

    /** Extract everything reachable about an offending slot. NOTE: the iframe's rendered content
     *  and any nested iframes are cross-origin and cannot be read from page JS (SecurityError) —
     *  only element attributes are available. The load chain is captured separately via resource timing. */
    private describeSlot(slot: HTMLElement, src: string, parsed: URL | null): SlotInfo {
        // Cap outerHTML so a bloated slot can't blow the beacon size limit.
        const outerHtml = slot.outerHTML.length > 2000 ? `${slot.outerHTML.slice(0, 2000)}…` : slot.outerHTML;
        return {
            key: slot.dataset.key ?? null,
            testId: slot.getAttribute('data-testid'),
            src,
            // ptgncdn/exoclick pass the zone key in the iframe src as ?key=… — a 2nd source of truth.
            srcKey: parsed?.searchParams.get('key') ?? null,
            host: parsed?.host ?? null,
            outerHtml,
        };
    }

    /** Summarize EVERY ad slot on the page. This is the reliable zone signal: on a real hit the
     *  offending iframe host is the allowlisted provider, so findOffendingSlot() returns null — but the
     *  true zone key is here on one of these <ins data-key> entries, alongside the iframe host, the
     *  key= param, and the sandbox (which tells us whether the creative could top-navigate at all). */
    private summarizeSlots(): AdSlotSummary[] {
        return Array.from(document.querySelectorAll<HTMLElement>(AD_SLOT_SELECTOR)).map((slot) => {
            const iframe = slot.querySelector('iframe');
            const src = iframe?.getAttribute('src') ?? null;
            let host: string | null = null;
            let iframeSrcKey: string | null = null;
            if (src) {
                try {
                    const parsed = new URL(src, window.location.href);
                    host = parsed.host || null;
                    iframeSrcKey = parsed.searchParams.get('key');
                } catch {
                    // Unparseable src → leave host null; the raw slot is still captured below.
                }
            }
            return {
                key: slot.dataset.key ?? null,
                testId: slot.getAttribute('data-testid'),
                iframeHost: host,
                iframeSrcKey,
                foreign: !!host && !isAllowedHost(host, this.options.allowedHosts),
                iframeSandbox: iframe?.getAttribute('sandbox') ?? null,
            };
        });
    }

    /** True if the page has pulled in a non-allowlisted resource or has a foreign-host ad iframe — i.e.
     *  something actually suspicious is present. The URL-less backstop vectors (focus-steal / pagehide)
     *  require this so a benign tab-switch on a clean page doesn't produce a report. Relies on
     *  resource-timing (observeResources): the forced redirect always fetches from its C2 before it fires. */
    private pageLooksSuspicious(): boolean {
        if (this.suspiciousResources.size > 0) return true;
        return this.summarizeSlots().some((slot) => slot.foreign);
    }

    /** True if focus currently sits on an iframe inside an ad slot — i.e. the user clicked the ad. The
     *  click happened inside the cross-origin frame so we never saw the pointerdown; this is how we tell
     *  a genuine ad click apart from a popup stealing focus. */
    private isAdIframeFocused(): boolean {
        const active = document.activeElement;
        return active instanceof HTMLIFrameElement && !!active.closest(AD_SLOT_SELECTOR);
    }

    private report(partial: Pick<MalvertisingHit, 'vector' | 'targetUrl' | 'targetHost'>): void {
        this.reported = true; // one report per pageview

        const slot = this.findOffendingSlot();
        const hit: MalvertisingHit = {
            zoneKey: slot.key,
            zoneTestId: slot.testId,
            creativeSrc: slot.src,
            creativeSrcKey: slot.srcKey,
            creativeHost: slot.host,
            slotOuterHtml: slot.outerHtml,
            suspiciousResources: Array.from(this.suspiciousResources),
            allSlots: this.summarizeSlots(),
            targetUrl: partial.targetUrl,
            targetHost: partial.targetHost,
            clickType: this.parseClickType(partial.targetUrl),
            vector: partial.vector,
            pageUrl: window.location.href,
            pageId: document.body?.dataset.pageId ?? null,
            msSinceLoad: Math.round(performance.now() - this.loadTime),
            userInteracted: this.userInteracted,
            avSegment: this.readAvSegment(),
            consentPolicy: this.readConsentPolicy(),
        };

        // Visible in console if anyone has devtools open, and captured by the agent's own
        // console telemetry once it is installed.
        const summary =
            `[MALVERTISING] forced redirect via ${hit.vector}: zone=${hit.zoneKey ?? 'unknown'} ` +
            `(${hit.zoneTestId ?? 'no-testid'}) → ${hit.targetHost}`;
        console.error(summary);

        // Report via the native TrackJS agent (lazy-loaded on a hit). The raw Capture API was
        // tried and confirmed NOT to render in this account's dashboard, so the agent — which
        // builds the payload shape the dashboard displays — is the only reliable path.
        this.trackViaAgent(hit, summary);

        // One-shot: detach listeners so we never double-report and don't leak handlers.
        this.stop();
    }

    /** Ensure the native TrackJS agent is installed, then track() with metadata. */
    private trackViaAgent(hit: MalvertisingHit, summary: string): void {
        const send = (): void => {
            try {
                const tjs = window.TrackJS;
                if (!tjs) return;
                for (const [key, value] of Object.entries(hit)) {
                    tjs.addMetadata(key, serializeMetaValue(value));
                }
                tjs.track(new Error(summary));
            } catch {
                // console.error above still stands.
            }
        };

        try {
            if (window.TrackJS?.isInstalled()) {
                send();
                return;
            }
            const script = document.createElement('script');
            script.src = this.options.trackJsAgentSrc;
            script.addEventListener('load', (): void => {
                try {
                    if (window.TrackJS && !window.TrackJS.isInstalled()) {
                        window.TrackJS.install({
                            token: this.options.trackJsToken,
                            application: this.options.application,
                        });
                    }
                    send();
                } catch {
                    // console.error above still stands.
                }
            });
            document.head.appendChild(script);
        } catch {
            // console.error above still stands.
        }
    }

    private readAvSegment(): string {
        try {
            const av = window.getAVCookie?.();
            if (!av) return 'none';
            return `${av.type}:required=${av.required}:guest=${av.isGuest}`;
        } catch {
            return 'unknown';
        }
    }

    private readConsentPolicy(): string | null {
        try {
            return window.getAdConsentPolicy?.() ?? null;
        } catch {
            return null;
        }
    }

    /** The campaign tags the redirect URL with ?click_type=hj|eval|pop to signal which mechanism
     *  fired. Extract it when we have a real target URL. */
    private parseClickType(targetUrl: string): string | null {
        try {
            return new URL(targetUrl, window.location.href).searchParams.get('click_type');
        } catch {
            return null;
        }
    }
}
